import {
  ProcessSupervisionRatingOption,
  ProcessSupervisionChunk,
  ProcessSupervisionRatingId,
  ProcessSupervisionRatingFieldId,
  displayToolParams,
} from '@scale/shared/types/chat/toolUseProcessSupervision';
import cn from 'classnames';
import _ from 'lodash';
import { Button, Markdown, Typography, Tag, SelectMenu, CodeEditorField } from '@scale/scaleui';
import { twMerge } from 'tailwind-merge';
import { useContext, useEffect, useMemo } from 'react';
import { useConfigureChatMessageDisplay } from 'react-scale/components/scalers/tasks/ThreadedInteraction/hooks/useConfigureChatMessageDisplay';
import { ChatTaskContext } from 'react-scale/components/scalers/tasks/ThreadedInteraction/contexts/ChatTaskContext';
import {
  Editor,
  EditorContainer,
  FooterContainer,
  MarkdownFloatingMenu,
  PlainTextEditorContext,
  WordCounter,
} from 'react-scale/components/scalers/tasks/ThreadedInteraction/components/editor';
import ExecutableCodeBlock from 'react-scale/components/scalers/tasks/ThreadedInteraction/coding/ExecutableCodeBlock';

import MarkdownEditorContext from 'react-scale/components/scalers/tasks/ThreadedInteraction/components/editor/MarkdownEditorContext';
import MarkdownToolbar from 'react-scale/components/scalers/tasks/ThreadedInteraction/components/editor/toolbar/MarkdownToolbar';

import ToolInputEditor from './ToolInputEditor';
import ToolUseTabbedCodeBlock from './ToolUseTabbedCodeBlock';
import logger from 'react-scale/lib/logging';
import { useMarkdownSyntaxOptions } from '../../../hooks/useMarkdownSyntaxOptions';

interface Props {
  chunk: Partial<ProcessSupervisionChunk>;
  ratingOptions: ProcessSupervisionRatingOption[];
  setChunk: (chunk: Partial<ProcessSupervisionChunk>) => void;
  disabled?: boolean;
  toolIds?: string[];
  selectedToolId?: string;
}

const ProcessSupervisionChunkStep: React.FC<Props> = ({
  chunk,
  ratingOptions,
  setChunk,
  disabled,
  toolIds,
  selectedToolId,
}) => {
  const { chatMessageDisplayConfig } = useConfigureChatMessageDisplay();
  const taskContext = useContext(ChatTaskContext);
  const markdownSyntaxConfig = useMarkdownSyntaxOptions();

  useEffect(() => {
    if (disabled) {
      clearRating();
    }
  }, [disabled]);

  const findRatingOption = (ratingId: ProcessSupervisionRatingId) => {
    const ratingOption = ratingOptions.find(option => option.id === ratingId);
    if (!ratingOption) {
      throw new Error('Rating option not found');
    }
    return ratingOption;
  };

  const { rating, value, originalValue } = useMemo(() => {
    if (!chunk.originalValue) {
      logger.error('Original value not found');
      return {
        rating: null,
        value: '',
        originalValue: '',
      };
    }
    if (!chunk.rating) {
      return {
        rating: null,
        value: chunk.rewriteValue ?? chunk.originalValue ?? '',
        originalValue: chunk.originalValue,
      };
    }
    return {
      rating: chunk.rating,
      value: chunk.rewriteValue ?? chunk.originalValue ?? '',
      originalValue: chunk.originalValue,
    };
  }, [chunk]);

  const ratingButtons = ratingOptions.map(option => ({
    ...option,
    isSelected: rating?.ratingId === option.id,
  }));

  const clearRating = () => {
    setChunk({
      ...chunk,
      rating: null,
    });
  };

  const setNewRating = ({ ratingId }: { ratingId: ProcessSupervisionRatingId }) => {
    const selectedRatingOption = findRatingOption(ratingId);
    if (!selectedRatingOption) {
      throw new Error('Rating option not found');
    }

    setChunk({
      ...chunk,
      rating: {
        ...selectedRatingOption,
        ratingId,
        fields: selectedRatingOption.fields.map(field => ({ ...field, value: '' })),
      },
    });
  };

  const setRewriteValue = ({ rewriteValue }: { rewriteValue: string }) => {
    setChunk({
      ...chunk,
      rewriteValue,
    });
  };

  const setFieldValue = ({
    fieldId,
    value,
  }: {
    fieldId: ProcessSupervisionRatingFieldId;
    value: string;
  }) => {
    if (!rating) {
      throw new Error('Rating not found');
    }
    const field = rating.fields.find(field => field.id === fieldId);
    if (!field) {
      throw new Error('Field not found');
    }
    setChunk({
      ...chunk,
      rating: {
        ...rating,
        fields: rating.fields.map(field => {
          if (field.id === fieldId) {
            return { ...field, value };
          }
          return field;
        }),
      },
    });
  };

  // TODO(@dannyadkins): clean this up
  const chunkEditorType = useMemo(() => {
    if (chunk.type === 'json') {
      return 'json';
    }
    if (chunk.type === 'plaintext' || chunk.name === 'Thought') {
      return 'plaintext';
    }
    if (chunk.type === 'markdown') {
      return 'markdown';
    }
    if (chunk.type === 'tool_select' || chunk.name === 'Action') {
      return 'tool_select';
    }
    if (chunk.type === 'tool_params_json') {
      return 'tool_params_json';
    }
    if (chunk.type === 'html') {
      return 'html';
    }
    if (chunk.type === 'html_rendered') {
      return 'html_rendered';
    } else if (chunk.type === 'base64_image') {
      return 'base64_image';
    }
  }, [chunk]);

  const getComponentToShowOriginalValue = () => {
    try {
      if (chunkEditorType === 'html_rendered') {
        const val = originalValue.replace('```html', '').replace(/(```)$/, '');
        return <iframe className="w-full h-[500px]" srcDoc={val} />;
      } else if (chunkEditorType === 'base64_image') {
        return <img src={`data:image/png;base64, ${originalValue}`} />;
      } else if (chunkEditorType === 'json') {
        const options = Object.entries(JSON.parse(originalValue)).map(([key, value]) => {
          const displayValue = _.isObject(value) ? JSON.stringify(value, null, 2) : value;
          return {
            id: key,
            label: (key === 'stdout' ? 'Output' : 'Error') ?? key,
            value: displayValue + '\n', // Add a new line so scrollbar does not cover the last line
          };
        });
        const defaultOption =
          options.find(option => option.id === 'stdout' && option.value.length) ||
          _.maxBy(options, option => option.value.length);
        if (!defaultOption) {
          throw new Error('Default option not found');
        }
        return (
          <ToolUseTabbedCodeBlock options={options} defaultTab={defaultOption.id} language="json" />
        );
      } else if (chunkEditorType === 'html') {
        const val = originalValue.replace('```html', '').replace(/(```)$/, '');
        return (
          <ExecutableCodeBlock
            code={val}
            codeBlocksInMarkdownText={[]}
            // @ts-ignore
            languageMatch={['html', 'html']}
            codeBlockIndex={0}
            loading={false}
            defaultExpanded={false}
          />
        );
      } else {
        const getVal = () => {
          if (!chunk.originalValue) {
            logger.error('Original value not found');
            return '';
          } else if (chunk.type === 'tool_params_json') {
            return displayToolParams(chunk.originalValue);
          } else if (chunk.display) {
            return chunk.display(chunk.originalValue);
          }
          return chunk.originalValue;
        };
        const val = getVal();
        return (
          <div className="w-full border border-neutral-300 rounded-md p-3">
            <Markdown
              className="text-sm break-words"
              markdown={val}
              enableMath={chatMessageDisplayConfig.markdownConfig.enableMathPlugin}
              enableHtml={taskContext.htmlMarkdownConfig?.enablePromptPreviews}
              enableSingleDollarTextMath={
                chatMessageDisplayConfig.markdownConfig.enableSingleDollarTextMath
              }
              enableSoftBreaks={true}
            />
          </div>
        );
      }
    } catch (e) {
      // eslint-disable-next-line no-console
      console.log(e);
      logger.error('Error displaying original value', e);
      return <div>An error occurred. Please try again in a few seconds.</div>;
    }
  };

  const getCodeEditor = ({
    value,
    language,
    readOnly,
    onChange,
  }: {
    value: string;
    language: 'html' | 'python' | 'json';
    readOnly: boolean;
    onChange?: (val: any) => void;
  }) => {
    return (
      <CodeEditorField
        className={cn('flex flex-row bg-[#2d2d30] rounded-b-lg', 'h-96 w-full')}
        value={value}
        onChangeValue={onChange}
        editorProps={{
          language,
          theme: 'vs-dark',
        }}
        // See https://microsoft.github.io/monaco-editor/docs.html#variables/editor.EditorOptions.html
        editorOptions={{
          fontSize: 14,
          minimap: { enabled: false },
          scrollBeyondLastLine: false,
          padding: {
            top: 19,
            bottom: 8,
          },
          formatOnPaste: true,
          formatOnType: true,
          renderLineHighlight: 'none',
          // Hovers are very whacky and take up the entire width of the screen - so we disable them for now
          hover: { enabled: false },
          // Inline suggestions take up a large proportion of the screen when the editor is in inline mode
          // Thus we should only enable this in full-screen mode
          // inlineSuggest: { enabled: fullScreenEnabled }
        }}
        disabled={readOnly}
      />
    );
  };

  return (
    <div>
      <div className="flex flex-col gap-4 w-full justify-between mb-4">
        {getComponentToShowOriginalValue()}

        <div className="max-w-[300px] flex self-end flex-wrap items-end flex-row-reverse gap-1">
          {!disabled &&
            ratingButtons.map(option => (
              <Button
                key={option.id}
                onClick={() => setNewRating({ ratingId: option.id })}
                variant={option.isSelected ? 'outline' : 'transparent'}
                className={twMerge(
                  `inline-flex px-2.5 py-1.5 text-sm items-center rounded-lg font-medium transition-all justify-center`,
                )}
                data-testid={`rating-option-${option.id}`}
              >
                <Typography.Subtitle3>{option.name}</Typography.Subtitle3>
              </Button>
            ))}
        </div>
      </div>
      {rating?.isRewriteEnabled && (
        <div className="flex flex-col gap-2">
          <Typography.Subtitle3>Rewrite the value until it is correct</Typography.Subtitle3>
          <div className="flex flex-col gap-2 items-start w-full">
            {chunkEditorType === 'plaintext' && (
              <PlainTextEditorContext initialContent={value}>
                <EditorContainer className="w-full">
                  <Editor
                    value={value}
                    onContentChange={content => setRewriteValue({ rewriteValue: content })}
                  />
                </EditorContainer>
              </PlainTextEditorContext>
            )}
            {chunkEditorType === 'markdown' && (
              <MarkdownEditorContext
                defaultTokens={markdownSyntaxConfig}
                initialContent={value}
                enableMath={chatMessageDisplayConfig.markdownConfig.enableMathPlugin}
              >
                <EditorContainer className="w-full">
                  <MarkdownToolbar
                    enableMath={chatMessageDisplayConfig.markdownConfig.enableMathPlugin}
                  />
                  <MarkdownFloatingMenu
                    enableMath={chatMessageDisplayConfig.markdownConfig.enableMathPlugin}
                  />
                  <Editor onContentChange={content => setRewriteValue({ rewriteValue: content })} />
                </EditorContainer>
              </MarkdownEditorContext>
            )}
            {chunkEditorType === 'html' &&
              getCodeEditor({
                value,
                language: 'html',
                readOnly: false,
                onChange: val => setRewriteValue({ rewriteValue: val }),
              })}
            {chunkEditorType === 'tool_select' && (
              <SelectMenu
                options={
                  toolIds?.map((option: any) => ({
                    id: option,
                    name: option,
                  })) || []
                }
                selectedOptionId={value}
                setSelected={(newValue: any) => setRewriteValue({ rewriteValue: newValue })}
                className="w-full"
              />
            )}

            {chunkEditorType === 'tool_params_json' && (
              <ToolInputEditor
                toolId={selectedToolId!}
                onChange={newValue => setRewriteValue({ rewriteValue: JSON.stringify(newValue) })}
                value={JSON.parse(value)}
                originalValue={JSON.parse(chunk?.originalValue || '{}')}
              />
            )}
          </div>
        </div>
      )}
      {rating?.fields.map(field => (
        <div key={field.id}>
          <Tag className="mb-4" onClick={function noRefCheck() {}} size="lg" variant="neutral">
            {field.name}
          </Tag>
          <MarkdownEditorContext
            defaultTokens={markdownSyntaxConfig}
            enableMath={chatMessageDisplayConfig.markdownConfig.enableMathPlugin}
            initialContent={field.value}
          >
            <EditorContainer>
              <MarkdownToolbar
                enableMath={chatMessageDisplayConfig.markdownConfig.enableMathPlugin}
              />
              <Editor
                onContentChange={content => setFieldValue({ fieldId: field.id, value: content })}
              />
              <FooterContainer>
                <WordCounter />
              </FooterContainer>
              <MarkdownFloatingMenu
                enableMath={chatMessageDisplayConfig.markdownConfig.enableMathPlugin}
              />
            </EditorContainer>
          </MarkdownEditorContext>
        </div>
      ))}
    </div>
  );
};

export default ProcessSupervisionChunkStep;
